/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  Application,
  AutomationConfig,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { AutomationConfig_UserStatus } from "@smartsuite/automation-engine-api";
import {
  FlashNotificationPosition,
  notifySuccess,
  notifyWarning,
} from "@smartsuite/flash-notifications";
import { PanelResizer, Scrollbar } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import * as R from "remeda";
import { isTruthy } from "remeda";
import { v4 as uuid } from "uuid";
import type { AuthAttempt } from "../../auth";
import { generateDescription } from "../../components/PhraseBuilder/helper";
import { ActivityBadge } from "../../elements/ActivityBadge/ActivityBadge";
import { ConfirmAutomationCard } from "../../elements/ConfirmModal/ConfirmAutomationCard";
import Modal from "../../elements/Modal/Modal";
import { TimeZoneBadge } from "../../elements/TimeZoneBadge/TimeZoneBadge";
import { useEngineClient } from "../../services/engineClient";
import { useSolutionDirectory } from "../../services/solution-directory";
import {
  updateActionDescription,
  updateTriggerDescription,
} from "../../services/updateDescription";
import * as A from "../../stores/automation/mergers/actions";
import type { TriggerMerger } from "../../stores/automation/mergers/trigger";
import * as TriggerStoreMerger from "../../stores/automation/mergers/trigger";
import { useRootStore } from "../../stores/hooks";
import type {
  ActionCandidate,
  ActionGroupType,
  TriggerCandidate,
} from "../../types/candidate";
import { collectActionSolutionIds } from "../../utils/action-solutions";
import {
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
  ROUTE_AUTOMATION_FORM,
  WHEN_BUTTON_IS_CLICKED_BUTTON_LABEL,
  WHEN_BUTTON_IS_CLICKED_BUTTON_SLUG,
  WHEN_BUTTON_IS_CLICKED_TRIGGER_ID,
} from "../../utils/constants";
import { duplicateInput } from "../../utils/duplicate-input";
import { useOnEscapeKeyPress } from "../../utils/hooks";
import { findInputAndPick, pickValueConfigString } from "../../utils/inputs";
import { AutomationActivityView } from "../AutomationActivity/AutomationActivityView/AutomationActivityView";
import { AuthUnit } from "./AuthUnit";
import {
  generateAutomationLabel,
  hasUnsavedChanges,
} from "./AutomationForm.helpers";
import { FlowCanvasPane } from "./FlowCanvasPane";
import { useConfirmModals } from "./ConfirmModals";
import { Details } from "./Details";
import { DuplicateModal } from "./DuplicateModal";
import { DynamicInputsForm } from "./DynamicInputsForm/DynamicInputsForm";
import { Header } from "./Header";
import {
  StyledActiveAutomationTitleInput,
  StyledActiveAutomationTitleWrapper,
  StyledAutomationTitleWrapper,
} from "./Header/style";
import { NotPermittedModal } from "./NotPermittedModal/NotPermittedModal";
import { ErrorSection } from "./Sections/ErrorSection";
import { SectionSwitch } from "./Switches/SectionSwitch";
import { clearAutomationInputs } from "./clear-empty-action-inputs";
import {
  ActionsLayout,
  EditAutomationContentInnerStyles,
  EditAutomationSidebarColumnStyles,
  EventContentColumnStyles,
  EventContentStyles,
  HeaderTitle,
  ModalContentStyles,
  StyledErrorText,
} from "./style";
import type { CurrSection } from "./types";
import { sectionForRoute } from "./active-section";
import { useSelectionUrlSync } from "./use-selection-url-sync";

/** Minimum width of the right settings panel. Sized so the conditions area
 * inside it can shrink to ~480px (its container-query stacking threshold) --
 * i.e. the panel can be dragged / auto-collapse narrow enough for condition
 * rows to switch to the stacked layout. */
const CONTENT_PANEL_MIN_WIDTH = 520;
/** Canvas column width for a normal (no Conditional Logic) automation. */
const DEFAULT_CANVAS_WIDTH = 520;
/** Minimum canvas column width; matches min-width on
 * EditAutomationSidebarColumnStyles so the state can't drift below the style. */
const CANVAS_MIN_WIDTH = 400;

export const AutomationForm = observer<{
  onClose?: () => void;
  currSection?: CurrSection;
}>(({ onClose, currSection }) => {
  const store = useRootStore();
  const engineClient = useEngineClient();
  const { loadForSolutions } = useSolutionDirectory();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const automationId = useParams().id;
  const historyId = useParams().historyId;
  const triggerParam = searchParams.get("trigger");
  const fieldSlugParam = searchParams.get("field_slug");
  const applicationParam = searchParams.get("application_id");
  const fieldLabelParam = searchParams.get("field_label");
  const { showConfirmationModal } = useConfirmModals();

  const candidate = store.automations.candidate;
  const [candidateBeforeEdit, setCandidateBeforeEdit] =
    useState<AutomationConfig>();
  const [authAttempt, setAuthAttempt] = useState<AuthAttempt | undefined>();
  const [sidebarWidth, setSidebarWidth] =
    useState<number>(DEFAULT_CANVAS_WIDTH);
  const [labelInput, setLabelInput] = useState("");
  const [activeItem, setActiveItem] = useState<CurrSection>(null);
  const [prevActiveItem, setPrevActiveItem] = useState<CurrSection>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  const [showLabelErrorMessage, setShowLabelErrorMessage] =
    useState<boolean>(false);
  const bodyRef = useRef<HTMLElement>(document.body);
  const titleInputRef = useRef(null);
  const sidebarColumnRef = useRef<HTMLDivElement>(null);
  const automationsList = store.automations.list;
  const integrations = store.integrations.list;
  const location = useLocation();

  // Members and teams are permission scoped per solution, but every member
  // picker and the pill validation read one shared list. An action can target a
  // table in another solution, so that solution's directory has to be loaded
  // too -- otherwise its members cannot be picked and members already saved on
  // the action render as "Unknown User" with a "Please remove invalid values"
  // error. Reading the ids during render keeps this reactive to the action's
  // solution being changed in the target picker.
  const actionSolutionIds = collectActionSolutionIds(candidate.allActions);
  const actionSolutionIdsKey = actionSolutionIds.join(",");

  useEffect(() => {
    // A target solution we are not allowed to read simply leaves its members
    // unselectable; it must not take the whole form down.
    void loadForSolutions(actionSolutionIds).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionSolutionIdsKey, loadForSolutions]);

  const isNewAutomation = useMemo(() => {
    return location.pathname === "/create";
  }, [location.pathname]);

  const sortedExistingLabels = useMemo(() => {
    return isNewAutomation
      ? automationsList
          .map((automation) => automation.label)
          .sort((a, b) => a.localeCompare(b))
      : automationsList
          .map((automation) => automation.label)
          .filter((label) => label !== candidate.self.label)
          .sort((a, b) => a.localeCompare(b));
  }, [candidate, automationsList, candidateBeforeEdit, isNewAutomation]);

  // PanelResizer drag handler. Respects the user's dragged width (clamped to
  // the valid range) for BOTH normal and Conditional Logic automations. Adding
  // a CL block still collapses the settings panel to its min once, via the
  // hasBranches effect below -- but the user must be able to drag it back
  // afterwards, so this no longer force-pins the canvas to max.
  const handleChangePanelWidth = (updatedWidth: number): void => {
    const viewportWidth = bodyRef.current.offsetWidth;
    const sidebarMaxWidth = viewportWidth - CONTENT_PANEL_MIN_WIDTH;
    setSidebarWidth(
      Math.max(CANVAS_MIN_WIDTH, Math.min(updatedWidth, sidebarMaxWidth)),
    );
  };

  const hasBranches = candidate.branches.hasBranches;
  const [animateSidebarWidth, setAnimateSidebarWidth] = useState(false);

  // spec: adding a Conditional Logic block collapses the settings panel to its
  // minimum width (the canvas column takes all remaining space); removing the
  // last one — or opening an automation that has none — returns the canvas to
  // its default width. Resetting on the false transition (instead of bailing
  // out) is what stops the expanded width from lingering on the next, branch-
  // less automation. Only fires on the CL<->non-CL transition, so a manually
  // resized / window-adapted width is left untouched otherwise.
  useEffect(() => {
    setAnimateSidebarWidth(true);
    setSidebarWidth((width) =>
      hasBranches
        ? Math.max(width, bodyRef.current.offsetWidth - CONTENT_PANEL_MIN_WIDTH)
        : DEFAULT_CANVAS_WIDTH,
    );
    const timer = window.setTimeout(() => setAnimateSidebarWidth(false), 400);
    return () => window.clearTimeout(timer);
  }, [hasBranches]);

  // On viewport resize keep the user's chosen width but clamp it into the new
  // valid range. A functional update reads the latest width (not a stale
  // closure), so the mount-time tick can no longer race the CL auto-expand
  // effect and shrink the canvas back down.
  useResizeDetector({
    targetRef: bodyRef,
    onResize: () => {
      const sidebarMaxWidth =
        bodyRef.current.offsetWidth - CONTENT_PANEL_MIN_WIDTH;
      setSidebarWidth((width) =>
        Math.max(CANVAS_MIN_WIDTH, Math.min(width, sidebarMaxWidth)),
      );
    },
  });

  const showOnOffModal = localStorage.getItem("showOnOffModal") === "true";

  const onChangeAction = (
    integrationId: string,
    actionId: string,
    actionGroupType: ActionGroupType,
  ): void => {
    if (activeItem?.type !== "action") return;

    const previous = candidate.allActions.find(
      (a) => a.hash === activeItem.hash,
    );
    const previousActionReference = previous?.actionReference;

    const nextHash = uuid();
    candidate.setActions(
      [
        A.ejectActionReferences(activeItem.hash),
        A.mergeReferenceIds(activeItem.hash, integrationId, actionId, nextHash),
      ],
      actionGroupType,
    );

    if (previousActionReference !== undefined) {
      candidate.looping.clearSourceIfActionReference(previousActionReference);
      candidate.branches.clearConditionsIfActionReference(
        previousActionReference,
      );
    }

    setActiveItem({ type: "action", hash: nextHash });
  };

  const goAutomationForm = (): void => {
    store.router.setPath(ROUTE_AUTOMATION_FORM);
  };

  // Keyed on the section TYPE, not the prop identity: the `:id/history` routes
  // pass a fresh `currSection` object literal on every App render, so keying on
  // the object re-asserted History over a selection the user had moved on to.
  useEffect(() => {
    if (currSection?.type === "history" && activeItem == null) {
      setPrevActiveItem({ type: "trigger", hash: "" });
    }
    const next = sectionForRoute(currSection, activeItem, isNewAutomation);
    if (next !== undefined) {
      setActiveItem(next);
    }
  }, [currSection?.type]);

  useEffect(() => {
    if (
      !isNewAutomation &&
      store.automations.automationsCallResolved &&
      store.credentials.isLoaded
    ) {
      const existCandidate = store.automations.list.find((a) => {
        return a.automationId === automationId;
      });

      if (
        existCandidate === undefined &&
        triggerParam === "button_click" &&
        isTruthy(fieldSlugParam) &&
        isTruthy(applicationParam) &&
        automationId !== undefined
      ) {
        // if the automation is not found, we need to create a new one
        const label = isTruthy(fieldLabelParam)
          ? fieldLabelParam
          : generateAutomationLabel(sortedExistingLabels);
        candidate.createEmptyAutomation(
          store.config.solutionId,
          store.config.userPreferences.timezone.value,
        );
        candidate.updateLabel(label);
        candidate.setAutomationId(automationId);
        candidate.setTrigger(
          ...generateWhenButtonIsClickedTrigger(
            integrations,
            applicationParam,
            fieldSlugParam,
            store.credentials.list.find(
              (c) =>
                c.authenticationMethodReference?.integrationId ===
                (integrations.find((i) => i.integrationId.includes("ssi"))
                  ?.integrationId ?? ""),
            )?.credentialId,
          ),
        );
        setIsButtonClicked(true);
        setCandidateBeforeEdit({
          ...candidate.self,
          actionGroups: [
            {
              instanceId: 0,
              config: {
                oneofKind: "actions",
                actions: {
                  actions: [],
                },
              },
            },
          ],
        });
        return;
      }
      if (existCandidate === undefined) {
        navigate("/list");
      }
      setCandidateBeforeEdit(existCandidate);
    }
  }, [
    store.automations.list.length,
    store.automations.automationsCallResolved,
    store.credentials.list.length,
    store.credentials.isLoaded,
  ]);

  useEffect(() => {
    let label = "";
    if (isNewAutomation) {
      candidate.createEmptyAutomation(
        store.config.solutionId,
        store.config.userPreferences.timezone.value,
      );

      label = generateAutomationLabel(sortedExistingLabels);
    } else {
      // temporary solution to fix ui glitches
      candidate.createEmptyAutomation(
        store.config.solutionId,
        store.config.userPreferences.timezone.value,
      );

      if (candidateBeforeEdit !== undefined) {
        candidate.cloneAndSetAutomation(candidateBeforeEdit);
      }
      label = candidate.self.label;
    }

    setLabelInput(label);
    candidate.updateLabel(label);
    store.scheduleStore.setSchedule(undefined);
    // React Flow canvas: reset zoom to 100% and clear per-modal UI state
    // (collapsed loops) whenever an automation is opened.
    store.flowCanvas.reset();
  }, [candidateBeforeEdit]);

  // Declared after the clone above: its effect must run once the clone is in.
  useSelectionUrlSync({
    automationId,
    historyId,
    routeIsHistory: currSection?.type === "history",
    isNewAutomation,
    // A new automation's draft is created by the same effect, on mount.
    isHydrated: isNewAutomation || candidateBeforeEdit !== undefined,
    getGroups: () => candidate.self.actionGroups,
    activeItem,
    prevActiveItem,
    setActiveItem,
    setPrevActiveItem,
    onActionNotFound: () =>
      notifyWarning(
        t("automation-form.linked-action-not-found"),
        FlashNotificationPosition.bottomLeft,
        5000,
      ),
  });

  useEffect(() => {
    if (!isNewAutomation && candidateBeforeEdit) {
      let triggerPromise: Promise<void> | undefined;
      if (candidate.self.trigger.credentialId !== "") {
        triggerPromise = updateTriggerDescription(
          candidate,
          false,
          engineClient,
          store.automations,
          store.integrations,
        );
      }

      const actionsPromises = candidate.allActions.map(
        (action: ActionCandidate) => {
          return updateActionDescription(
            candidate,
            action,
            store.integrations.list,
            engineClient,
            store.automations,
          );
        },
      );
      void Promise.all([triggerPromise, ...actionsPromises].filter(isTruthy));
    }
  }, [candidate, store.integrations.list, candidateBeforeEdit]);

  const [showTitleInput, setShowTitleInput] = useState<boolean>(false);

  const goBack = useCallback((): void => {
    candidate.removeSubmitted();
    if (
      store.automations.getAutomationsWithValidTrigger(store.applications.list)
        .length !== 0
    ) {
      navigate("/list");
    } else {
      onClose?.();
    }
  }, [candidate, store.router]);

  const showUnsavedChangesModal = (
    forDuplicatingAutomation?: AutomationConfig,
  ): void => {
    store.modal.openConfirmModal({
      type: "warning",
      title: t("confirm-modal.are-you-sure"),
      description: t("confirm-modal.cancel-changes-description"),
      negativeButtonText: t("confirm-modal.exit-without-saving"),
      positiveButtonText: t("confirm-modal.save-changes"),
      hasUnsavedChanges: true,
      onClose: () => {
        store.modal.closeConfirmModal();
        if (R.isTruthy(forDuplicatingAutomation)) {
          createDuplicate(forDuplicatingAutomation);
        } else {
          goBack();
        }
      },
      onConfirm: () => {
        store.modal.closeConfirmModal();
        if (labelInput.length > 0) {
          onSubmit(forDuplicatingAutomation);
        }
      },
    });
  };

  const onCloseForm = (): void => {
    if (
      hasUnsavedChanges({
        labelInput,
        candidate: candidate.self,
        candidateBeforeEdit,
      })
    ) {
      showUnsavedChangesModal();
    } else {
      goBack();
    }
  };

  const getAppsForActionsInAnotherSolutions = async (): Promise<
    Application[]
  > => {
    return (
      await Promise.all(
        candidate.allActions.map(async (action) => {
          if (
            action.actionReference?.actionId === "create-record" ||
            action.actionReference?.actionId === "update-record"
          ) {
            // for now it's only for CreateRecord
            const solutionId = findInputAndPick(
              action.inputs,
              INPUT_SOLUTION_ID,
              pickValueConfigString,
            );
            if (
              solutionId !== undefined &&
              solutionId !== store.config.solutionId
            ) {
              const applicationId = findInputAndPick(
                action.inputs,
                INPUT_APPLICATION_ID,
                pickValueConfigString,
              );
              if (applicationId !== undefined) {
                return await engineClient
                  .getApplications(solutionId)
                  .then((apps) =>
                    apps.applications.find((a) => a.id === applicationId),
                  );
              }
            }
          }
        }),
      )
    ).filter((a): a is Application => !!a);
  };

  const submit = async (
    forDuplicatingAutomation?: AutomationConfig,
  ): Promise<void> => {
    const members = store.members.list;
    const teams = store.teams.list;
    const additionalApplications = await getAppsForActionsInAnotherSolutions();
    candidate.setAutoDescription(
      generateDescription(
        candidate.self,
        [...store.applications.list, ...additionalApplications],
        integrations,
        members,
        teams,
      ),
    );
    setIsSaving(true);
    if (isNewAutomation || isButtonClicked) {
      void candidate
        .release()
        .then((automation) =>
          Promise.all([
            automation,
            engineClient.createAutomation({ automation }),
          ]),
        )
        .then(([automation, { automationId, firstCreated }]) => {
          store.automations.mergeItem({
            ...automation,
            automationId,
            firstCreated,
          });
          if (R.isTruthy(forDuplicatingAutomation)) {
            createDuplicate(forDuplicatingAutomation);
          } else {
            goBack();
          }
        })
        .finally(() => setIsSaving(false));
    } else {
      void candidate
        .release()
        .then((automation) =>
          Promise.all([
            automation,
            engineClient.updateAutomation({ automation }),
          ]),
        )
        .then(([automation, { lastUpdated, systemStatus }]) => {
          store.automations.updateItem({
            ...automation,
            lastUpdated,
            systemStatus: systemStatus ?? automation.systemStatus,
          });
          if (R.isTruthy(forDuplicatingAutomation)) {
            createDuplicate(forDuplicatingAutomation);
          } else {
            goBack();
          }
        })
        .finally(() => setIsSaving(false));
    }
  };

  const onSubmit = (forDuplicatingAutomation?: AutomationConfig): void => {
    if (showLabelErrorMessage) {
      return;
    }

    showConfirmationModal(showOnOffModal, submit, forDuplicatingAutomation);
  };

  const saveLabelChanges = (label: string): void => {
    if (label.length === 0) {
      setShowTitleInput(true);
      showUnsavedChangesModal();
    } else {
      setShowTitleInput(false);
      const currentAutomation = automationsList.find(
        (automation) => automation.automationId === automationId,
      );

      const isLabelAlreadyExists = sortedExistingLabels.some(
        (existingLabel) =>
          existingLabel !== currentAutomation?.label && existingLabel === label,
      );

      if (isLabelAlreadyExists) {
        setShowLabelErrorMessage(true);
      } else {
        setShowLabelErrorMessage(false);
        setLabelInput(label);
        candidate.updateLabel(label);
      }
    }
  };

  const deleteAutomation = (automationId: string): void => {
    store.modal.modal.isLoading = true;
    void engineClient
      .deleteAutomation(automationId)
      .then(() => store.automations.deleteItem(automationId))
      .finally(() => {
        store.modal.modal.isLoading = false;
        store.modal.closeConfirmModal();
        goBack();
      });
  };

  const toggleStatus = (checked: boolean): void => {
    candidate.updateStatus(
      checked
        ? AutomationConfig_UserStatus.USER_ENABLED
        : AutomationConfig_UserStatus.USER_DISABLED,
    );
    if (!isNewAutomation && candidateBeforeEdit) {
      void candidate
        .release(true)
        .then(
          async (automation) =>
            await Promise.all([
              automation,
              engineClient.updateAutomation({ automation }),
            ]),
        )
        .then(([automation, { lastUpdated, systemStatus }]) => {
          store.automations.updateItem({
            ...automation,
            lastUpdated,
            systemStatus: systemStatus ?? automation.systemStatus,
          });
        });
    }
  };

  const createDuplicate = (automation: AutomationConfig): void => {
    void engineClient
      .createAutomation({ automation })
      .then(({ automationId, firstCreated }) => {
        const createdAutomation = {
          ...automation,
          automationId,
          firstCreated,
          lastUpdated: undefined,
        };
        store.automations.addItem(createdAutomation);
        store.automations.candidate.cloneAndSetAutomation(createdAutomation);
        goAutomationForm();
      });
  };

  const onDuplicate = (newLabel: string): void => {
    setIsDuplicating(false);
    if (candidate.self.automationId !== "") {
      let duplicatedAutomation = {
        ...candidate.self,
        automationId: "",
        label: newLabel,
      };
      const trigger: TriggerCandidate = {
        ...duplicatedAutomation.trigger,
        inputs:
          duplicatedAutomation.trigger?.inputs.map((input) => {
            return duplicateInput(input);
          }) ?? [],
      };
      duplicatedAutomation = {
        ...duplicatedAutomation,
        trigger,
      };

      clearAutomationInputs(duplicatedAutomation);
      if (
        hasUnsavedChanges({
          labelInput: newLabel,
          candidate: candidate.self,
          candidateBeforeEdit,
        })
      ) {
        showUnsavedChangesModal(duplicatedAutomation as AutomationConfig);
      } else {
        createDuplicate(duplicatedAutomation as AutomationConfig);
      }
    }
  };

  const onCopyLink = (): void => {
    void window.navigator.clipboard.writeText(window.location.href).then(() => {
      notifySuccess("Link copied", FlashNotificationPosition.bottomLeft, 3000);
    });
  };

  const onDelete = (): void => {
    if (!isNewAutomation && candidateBeforeEdit) {
      store.modal.openConfirmModal({
        type: "warning",
        title:
          candidate.self.trigger?.triggerReference?.triggerId ===
          WHEN_BUTTON_IS_CLICKED_TRIGGER_ID
            ? t("confirm-modal.delete-automation-button-title")
            : t("confirm-modal.delete-automation-title"),
        size: "M",
        body: (
          <ConfirmAutomationCard
            automation={candidate.self as AutomationConfig}
            toggleStatus={toggleStatus}
          />
        ),
        negativeButtonText: t("confirm-modal.no-cancel"),
        positiveButtonText:
          candidate.self.trigger?.triggerReference?.triggerId ===
          WHEN_BUTTON_IS_CLICKED_TRIGGER_ID
            ? t("confirm-modal.yes-delete-automation-button")
            : t("confirm-modal.yes-delete-automation"),
        onClose: () => {
          store.modal.closeConfirmModal();
        },
        onConfirm: () => {
          deleteAutomation(candidate.self.automationId);
        },
      });
    }
  };

  useOnEscapeKeyPress(() => {
    setLabelInput(candidate.self.label);
    setShowTitleInput(false);
  });

  const isPermitted =
    candidate.self.systemStatus === undefined ||
    candidate.self.systemStatus?.systemStatus.oneofKind !== "notPermitted";

  if (!isPermitted) {
    return (
      <NotPermittedModal
        onClose={() => {
          navigate("/list");
        }}
      />
    );
  }

  return (
    <>
      <Modal
        header={
          <Header
            isInputActive={showTitleInput}
            actions={
              <>
                <ActionsLayout>
                  <TimeZoneBadge automation={candidate.self}></TimeZoneBadge>
                  <ActivityBadge
                    onClick={() => {
                      setPrevActiveItem(
                        activeItem != null
                          ? activeItem
                          : { type: "trigger", hash: "" },
                      );
                      setActiveItem({
                        type: "history",
                        hash: "",
                      });
                    }}
                  />
                </ActionsLayout>
              </>
            }
            modalTitle={
              <>
                {showTitleInput ? (
                  <StyledActiveAutomationTitleWrapper>
                    <StyledActiveAutomationTitleInput
                      ref={titleInputRef}
                      type="text"
                      value={labelInput}
                      maxLength={50}
                      onChange={(e) => {
                        setLabelInput(e.target.value);
                      }}
                      onBlur={() => {
                        saveLabelChanges(labelInput);
                      }}
                      onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.code === "Enter") {
                          saveLabelChanges(labelInput);
                        }
                      }}
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus={showTitleInput}
                    />
                  </StyledActiveAutomationTitleWrapper>
                ) : (
                  <StyledAutomationTitleWrapper
                    onClick={() =>
                      !candidate.showErrorSection && setShowTitleInput(true)
                    }
                  >
                    <HeaderTitle>{labelInput}</HeaderTitle>

                    {showLabelErrorMessage && (
                      <StyledErrorText>
                        {t("general.unique-automation-name")}
                      </StyledErrorText>
                    )}
                  </StyledAutomationTitleWrapper>
                )}
              </>
            }
            onRename={() => setShowTitleInput(true)}
            onDelete={onDelete}
            onDuplicate={() => {
              setIsDuplicating(true);
            }}
            onCopyLink={onCopyLink}
            toggleStatus={toggleStatus}
            onClose={onCloseForm}
          />
        }
        title={
          isNewAutomation
            ? t("automation-form.modal-create-title")
            : t("automation-form.modal-edit-title")
        }
        footerLine={
          candidate.showErrorSection ? null : (
            <Details automation={candidate.self} />
          )
        }
        positiveButtonText={
          isNewAutomation
            ? t("general.add-automation")
            : t("general.save-automation")
        }
        handleNegativeButton={onCloseForm}
        handlePositiveButton={() => {
          onSubmit();
        }} // to avoid event as argument
        handleClose={onCloseForm}
        isDisabledNegative={isSaving}
        isDisabledPositive={isActionLoading}
        isLoading={isSaving}
      >
        <ModalContentStyles>
          {candidate.showErrorSection ? (
            <ErrorSection handleGoBack={onCloseForm} />
          ) : (
            <EditAutomationContentInnerStyles>
              <EditAutomationSidebarColumnStyles
                data-testid="canvas-column"
                style={{ width: sidebarWidth }}
                $animateWidth={animateSidebarWidth}
                ref={sidebarColumnRef}
              >
                <FlowCanvasPane
                  activeItem={activeItem}
                  prevActiveItem={prevActiveItem}
                  setActiveItem={setActiveItem}
                  onChangeAction={onChangeAction}
                />

                <PanelResizer
                  growDirection="slide-to-right"
                  panelRef={sidebarColumnRef}
                  width={sidebarWidth}
                  onWidthChanged={handleChangePanelWidth}
                />
              </EditAutomationSidebarColumnStyles>

              {activeItem?.type === "history" ? (
                <AutomationActivityView
                  sidebarWidth={sidebarWidth}
                  goBack={() => {
                    setActiveItem(prevActiveItem);
                  }}
                  jobId={historyId}
                />
              ) : (
                <EventContentColumnStyles>
                  <Scrollbar
                    style={{ height: "100%" }}
                    gutterTop={5}
                    gutterRight={5}
                    gutterBottom={5}
                    noScrollX
                  >
                    <EventContentStyles>
                      <SectionSwitch
                        section={activeItem}
                        setAuthAttempt={setAuthAttempt}
                        setSection={setActiveItem}
                        setIsActionLoading={setIsActionLoading}
                      />
                    </EventContentStyles>
                  </Scrollbar>
                </EventContentColumnStyles>
              )}
            </EditAutomationContentInnerStyles>
          )}
        </ModalContentStyles>
      </Modal>
      {R.isTruthy(authAttempt) &&
        (authAttempt?.authMethod.type.oneofKind === "dynamicInputs" ? (
          <DynamicInputsForm
            // updateActionDescription={updateActionDescription}
            authAttempt={authAttempt}
            setAuthAttempt={setAuthAttempt}
            integrationTitle={
              integrations.find(
                (i) => i.integrationId === authAttempt?.integrationId,
              )?.label
            }
          />
        ) : (
          <AuthUnit
            updateActionDescription={updateActionDescription}
            authAttempt={authAttempt}
            setAuthAttempt={setAuthAttempt}
            integrationTitle={
              integrations.find(
                (i) => i.integrationId === authAttempt?.integrationId,
              )?.label
            }
          />
        ))}
      {isDuplicating && (
        <DuplicateModal
          label={candidate.self.label}
          onCancel={() => {
            setIsDuplicating(false);
          }}
          onDuplicate={onDuplicate}
        />
      )}
    </>
  );
});
const generateWhenButtonIsClickedTrigger = (
  integrations: IntegrationDescription[],
  applicationId: string,
  fieldSlug: string,
  credentialId?: string,
): TriggerMerger[] => {
  return [
    TriggerStoreMerger.mergeReferenceIds(
      integrations.find((i) => i.integrationId.includes("ssi"))
        ?.integrationId ?? "",
      WHEN_BUTTON_IS_CLICKED_TRIGGER_ID,
    ),
    TriggerStoreMerger.mergeInputById({
      inputId: INPUT_APPLICATION_ID,
      value: {
        values: [
          {
            value: {
              oneofKind: "string",
              string: applicationId,
            },
          },
        ],
      },
    }),
    TriggerStoreMerger.mergeInputById({
      inputId: WHEN_BUTTON_IS_CLICKED_BUTTON_SLUG,
      value: {
        values: [
          {
            value: {
              oneofKind: "string",
              string: fieldSlug,
            },
          },
        ],
      },
    }),
    TriggerStoreMerger.mergeInputById({
      inputId: WHEN_BUTTON_IS_CLICKED_BUTTON_LABEL,
      value: {
        values: [
          {
            value: {
              oneofKind: "string",
              string: "",
            },
          },
        ],
      },
    }),
    TriggerStoreMerger.mergeCredentialId(credentialId ?? ""),
  ];
};
AutomationForm.displayName = "AutomationForm";
